<template>
    <ModalDialogContainer class="modal-container-opaque" ref="modalContainer" :display="true" @keydown="escapeToClose">
        <div class="modal-dialog modal-md" role="document" @click="stopPropagationEvent">
            <div class="modal-content">
                <div class="modal-header">
                    <div class="modal-title">{{ $t("Join Room") }}</div>
                    <button type="button" class="btn-close" :title="$t('Close')" @click="close" :disabled="busy"></button>
                </div>

                <!-- Guest access disabled; keep for possible reactivation.
                <div class="modal-body" v-if="mode === 'select'">
                    <p class="mb-4">{{ $t("How would you like to join?") }}</p>
                    <div class="d-flex flex-column gap-3">
                        <button type="button" class="btn btn-primary" @click="mode = 'guest'" :disabled="busy">
                            {{ $t("Join as Guest") }}
                        </button>
                        <button type="button" class="btn btn-cancel" @click="mode = 'login'" :disabled="busy">
                            {{ $t("Log In / Register") }}
                        </button>
                    </div>
                </div>

                <form @submit="submitGuest" v-else-if="mode === 'guest'">
                    <div class="modal-body">
                        <p class="mb-3">{{ $t("Enter your display name") }}</p>
                        <div class="mb-4">
                            <input type="text" v-model="guestName" class="form-control" :disabled="busy" required
                                placeholder="Anonymous" autofocus />
                        </div>
                        <div class="mb-3 signup-legal-consent">
                            <div class="form-check d-flex align-items-start gap-2 p-0 m-0">
                                <input id="room-guest-legal-consent" class="form-check-input mt-1" type="checkbox"
                                    v-model="acceptGuestLegal" :disabled="busy">
                                <label class="form-check-label" for="room-guest-legal-consent">
                                    {{ $t("I accept the") }}
                                    <router-link class="legal-consent-link" :to="{ name: 'terms' }">{{ $t("Terms of use") }}</router-link>
                                    {{ $t("and the") }}
                                    <router-link class="legal-consent-link" :to="{ name: 'privacy' }">{{ $t("Privacy policy") }}</router-link>
                                </label>
                            </div>
                            <div class="form-error text-danger mt-2" v-if="errorGuestLegal">{{ errorGuestLegal }}</div>
                        </div>
                        <div v-if="error" class="form-error text-danger mb-3">{{ error }}</div>
                    </div>
                    <div class="modal-footer">
                        <button type="submit" class="btn btn-primary" :disabled="busy">
                            <span v-if="busy" class="spinner-border spinner-border-sm me-2" role="status"
                                aria-hidden="true"></span>
                            {{ $t("Join") }}
                        </button>
                        <button type="button" class="btn btn-cancel" @click="mode = 'select'" :disabled="busy">
                            {{ $t("Back") }}
                        </button>
                    </div>
                </form>
                -->

                <form @submit="submitLogin" v-if="mode === 'login'">
                    <div class="modal-body">
                        <div v-if="tpServices.length > 0" class="mb-3">
                            <a type="button" class="btn btn-outline-primary w-100 mb-2" v-for="tp of tpServices"
                                :key="tp.id" :href="tp.url">
                                <i v-if="tp.name === 'Google'" class="fa-brands fa-google me-2"></i>
                                {{ $t("Sign in with") }} {{ tp.name }}
                            </a>
                            <div class="d-flex align-items-center my-3">
                                <div class="border-top flex-grow-1"></div>
                                <div class="px-3 text-muted small">{{ $t("OR") }}</div>
                                <div class="border-top flex-grow-1"></div>
                            </div>
                        </div>

                        <div class="mb-3">
                            <label class="form-label mb-1">{{ $t("Username") }}</label>
                            <input type="text" v-model="loginUsername" class="form-control auto-focus" :disabled="busy" required
                                autofocus />
                        </div>
                        <div class="mb-4">
                            <label class="form-label mb-1">{{ $t("Password") }}</label>
                            <PasswordInput v-model:val="loginPassword" :disabled="busy"></PasswordInput>
                        </div>
                        <div v-if="error" class="form-error text-danger mb-3">{{ error }}</div>
                    </div>

                    <div class="modal-footer">
                        <button type="submit" class="btn btn-primary" :disabled="busy">
                            <span v-if="busy" class="spinner-border spinner-border-sm me-2" role="status"
                                aria-hidden="true"></span>
                            {{ $t("Log In") }}
                        </button>
                        <!-- Guest access disabled; keep for possible reactivation.
                        <button type="button" class="btn btn-cancel" @click="mode = 'select'" :disabled="busy">
                            {{ $t("Back") }}
                        </button>
                        -->
                    </div>
                </form>

            </div>
        </div>
    </ModalDialogContainer>
</template>

<script lang="ts">
import { defineComponent } from 'vue';
import ModalDialogContainer from "@/components/utils/ModalDialogContainer.vue";
import PasswordInput from "@/components/utils/PasswordInput.vue";
import { AuthController } from "@/control/auth";
import { RoomController } from "@/control/room";
import { Request } from "@asanrom/request-browser";
import { ApiAuth } from "@/api/api-group-auth";
import { ApiRoom } from "@/api/api-group-room";
import { getUniqueStringId } from "@/utils/unique-id";
import { Timeouts } from "@/utils/timeout";

export default defineComponent({
    name: "RoomAccessModal",
    components: {
        ModalDialogContainer,
        PasswordInput
    },
    emits: ["authenticated", "close"],
    setup() {
        return {
            requestId: getUniqueStringId(),
            tpLoadRequestId: getUniqueStringId(),
        };
    },
    data() {
        return {
            // Guest access disabled: open login directly. Keep for possible reactivation.
            mode: "login" as "select" | "guest" | "login",
            // guestName: "",
            loginUsername: "",
            loginPassword: "",
            // acceptGuestLegal: false,
            // errorGuestLegal: "",
            busy: false,
            error: "",
            tpServices: [] as any[]
        };
    },
    methods: {
        completeRoomAccess() {
            const routeRoomId = ((this.$route && this.$route.params && this.$route.params.roomId) || "") + "";
            const routeCode = ((this.$route && this.$route.query && this.$route.query.code) || "") + "";
            const pendingRoomId = RoomController.pendingJoinRoomId || "";
            const pendingCode = RoomController.pendingJoinAccessCode || "";
            const fallbackRoomId = RoomController.room || "";
            const fallbackCode = RoomController.accessCode || "";

            const targetRoomId = routeRoomId || pendingRoomId || fallbackRoomId;
            const targetCode = routeCode || pendingCode || fallbackCode;

            // Clear pending join info
            RoomController.pendingJoinRoomId = "";
            RoomController.pendingJoinAccessCode = "";

            if (!targetRoomId) {
                this.$emit("authenticated");
                return;
            }

            // Redirect to the URL the user was trying to access (e.g. /room/xxx/section/call/), not just the room
            const redirectPath = RoomController.pendingJoinRedirectPath || "";
            const pathBelongsToRoom = redirectPath && redirectPath.startsWith(`/room/${targetRoomId}`);
            const targetPath = pathBelongsToRoom ? redirectPath : null;
            RoomController.pendingJoinRedirectPath = "";

            const currentRouteName = (this.$route && this.$route.name) ? String(this.$route.name) : "";
            const currentRouteRoomId = ((this.$route && this.$route.params && this.$route.params.roomId) || "") + "";
            const shouldNavigateToRoom = currentRouteName !== "room" || currentRouteRoomId !== targetRoomId;
            const shouldNavigateToPath = targetPath && (this.$route.fullPath !== targetPath);

            if (shouldNavigateToPath) {
                const pathOnly = targetPath.split("?")[0];
                const query = targetCode ? { ...(this.$route.query || {}), code: targetCode } : (this.$route.query || {});
                this.$router.push({ path: pathOnly, query });
            } else if (shouldNavigateToRoom && !targetPath) {
                this.$router.push({
                    name: "room",
                    params: { roomId: targetRoomId },
                    query: targetCode ? { code: targetCode } : {},
                });
            }

            if (targetCode) {
                // For private-code rooms: first try joining with the code, then load room
                Request.Do(ApiRoom.GetRoomsJoinRoom(targetRoomId, { code: targetCode }))
                    .onSuccess((response) => {
                        if (response.canJoin) {
                            // Code accepted, now join the room
                            RoomController.joinRoom(targetRoomId, targetCode);
                        } else if (response.banned) {
                            this.error = this.$t("You are banned from this room.");
                        } else {
                            // Code didn't help (maybe public room with code param, or wrong code)
                            // Try joining directly anyway
                            RoomController.joinRoom(targetRoomId, targetCode);
                        }
                    })
                    .onRequestError(() => {
                        // Join endpoint failed, try joining directly
                        RoomController.joinRoom(targetRoomId, targetCode);
                    })
                    .onUnexpectedError(() => {
                        RoomController.joinRoom(targetRoomId, targetCode);
                    });
            } else {
                // Public or private room without code, just join directly
                RoomController.joinRoom(targetRoomId);
            }

            this.$emit("authenticated");
        },
        escapeToClose(e: KeyboardEvent) {
            if (e.key === "Escape") {
                this.close();
            }
        },
        stopPropagationEvent(e: Event) {
            e.stopPropagation();
        },
        close() {
            if (!this.busy) {
                this.$emit("close");
            }
        },
        loadTp() {
            Timeouts.Abort(this.tpLoadRequestId);
            Request.Pending(this.tpLoadRequestId, ApiAuth.ThirdPartyLoginDetails())
                .onSuccess((tp) => {
                    this.tpServices = tp;
                })
                .onRequestError((err, handleErr) => {
                    handleErr(err, {
                        temporalError: () => {
                            Timeouts.Set(this.tpLoadRequestId, 1500, this.loadTp.bind(this));
                        },
                    });
                })
                .onUnexpectedError(() => {
                    Timeouts.Set(this.tpLoadRequestId, 1500, this.loadTp.bind(this));
                });
        },
        // Guest access disabled; keep for possible reactivation.
        // submitGuest(e: Event) {
        //     e.preventDefault();
        //     if (this.busy) return;
        //     this.error = "";
        //     this.errorGuestLegal = "";
        //
        //     if (!this.acceptGuestLegal) {
        //         this.errorGuestLegal = this.$t("You must accept the Terms of use and Privacy policy");
        //         return;
        //     }
        //
        //     this.busy = true;
        //
        //     this.$getCaptcha("login").then((captcha) => {
        //         Request.Pending(this.requestId, ApiAuth.PostAuthAnonymous({ name: this.guestName, captcha: captcha }))
        //             .onSuccess((response) => {
        //                 this.busy = false;
        //                 AuthController.SetSessionAnon(response.name, response.token, response.uid);
        //                 this.completeRoomAccess();
        //             })
        //             .onCancel(() => { this.busy = false; })
        //             .onRequestError((err, handleErr) => {
        //                 this.busy = false;
        //                 handleErr(err, {
        //                     badRequestCaptcha: () => { this.error = this.$t("Please, prove you are not a robot by solving the captcha"); },
        //                     badRequest: () => { this.error = this.$t("Invalid display name"); },
        //                     forbiddenBlocked: () => { this.error = this.$t("Your account is banned."); },
        //                     forbiddenCooldown: () => { this.error = this.$t("For security reasons, wait 10 seconds to try again."); },
        //                     forbidden: () => { this.error = this.$t("Access denied"); },
        //                     serverError: () => { this.error = this.$t("Internal server error"); },
        //                     networkError: () => { this.error = this.$t("Could not connect to the server"); },
        //                 });
        //             })
        //             .onUnexpectedError((err) => {
        //                 this.error = err.message;
        //                 console.error(err);
        //                 this.busy = false;
        //             });
        //     });
        // },
        submitLogin(e: Event) {
            e.preventDefault();
            if (this.busy) return;
            this.busy = true;
            this.error = "";
            this.$getCaptcha("login").then((captcha) => {
                Request.Pending(this.requestId, ApiAuth.Login({ username: this.loginUsername, password: this.loginPassword, captcha: captcha, remember: true }))
                    .onSuccess((response) => {
                        this.busy = false;
                        AuthController.SetSession(response.session_id);
                        this.completeRoomAccess();
                    })
                    .onCancel(() => { this.busy = false; })
                    .onRequestError((err, handleErr) => {
                        this.busy = false;
                        handleErr(err, {
                            badRequestCaptcha: () => { this.error = this.$t("Please, prove you are not a robot by solving the captcha"); },
                            badRequestInvalidCredentials: () => { this.error = this.$t("Invalid username or password"); },
                            badRequest: () => { this.error = this.$t("Invalid username or password"); },
                            forbiddenCaptcha: () => { this.error = this.$t("Please, prove you are not a robot by solving the captcha"); },
                            forbiddenInvalidCredentials: () => { this.error = this.$t("Invalid username or password"); },
                            forbiddenUserBanned: () => { this.error = this.$t("The user you are trying to log into is banned from the platform"); },
                            forbidden: () => { this.error = this.$t("Invalid username or password"); },
                            serverError: () => { this.error = this.$t("Internal server error"); },
                            networkError: () => { this.error = this.$t("Could not connect to the server"); },
                        });
                    })
                    .onUnexpectedError((err) => {
                        this.error = err.message;
                        console.error(err);
                        this.busy = false;
                    });
            });
        }
    },
    mounted() {
        this.loadTp();
        this.$autoFocus();
    },
    beforeUnmount() {
        Request.Abort(this.requestId);
        Request.Abort(this.tpLoadRequestId);
        Timeouts.Abort(this.tpLoadRequestId);
    }
});
</script>

<style scoped>
.form-control.auto-focus {
    border: none !important;
    border-bottom: 2px solid var(--red-maya) !important;
    border-radius: 0 !important;
    color: var(--black-maya) !important;
    background-color: transparent !important;
}

.signup-legal-consent .legal-consent-link {
    color: var(--red-maya) !important;
    font-weight: 500;
}

.signup-legal-consent .legal-consent-link:hover,
.signup-legal-consent .legal-consent-link:focus-visible {
    color: var(--red-maya-dark) !important;
}

.signup-legal-consent .form-check {
    min-height: auto;
}

.signup-legal-consent .form-check-input {
    appearance: auto;
    -webkit-appearance: checkbox;
    opacity: 1;
    visibility: visible;
    float: none;
    flex: 0 0 auto;
    width: 18px;
    height: 18px;
    margin: 2px 0 0;
    border: 1px solid var(--red-maya);
    background-color: #fff;
}

.signup-legal-consent .form-check-input:checked {
    background-color: var(--red-maya);
    border-color: var(--red-maya);
}

.dark-theme .signup-legal-consent .form-check-input {
    background-color: #1f1f1f;
    border-color: color-mix(in srgb, var(--red-maya) 70%, white 30%);
}
</style>
